import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { InputComponent } from "../../shared/input/input.component";
import { ButtonComponent } from "../../shared/button/button.component";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { LayoutComponent } from "../../shared/layout/layout.component";
import { TitleComponent } from "../../shared/title/title.component";
import { GigyaService } from "../../services/gigya.service";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { BreadcrumbComponent } from "../../shared/breadcrumb/breadcrumb.component";
import { PasswordService, PasswordApiError } from "../../services/password.service";
import { PasswordRequirementsComponent } from "../../shared/password-requirements/password-requirements.component";
import { CredentialHandoffService } from "../../services/credential-handoff.service";
import { ActivationEmailService, ActivationEmailApiError } from "../../services/activation-email.service";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-onboarding-password",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputComponent,
    ButtonComponent,
    RouterLink,
    LayoutComponent,
    TitleComponent,
    BreadcrumbComponent,
    PasswordRequirementsComponent,
    SpinnerComponent,
  ],
  templateUrl: "./onboarding-password.component.html",
  styleUrl: "./onboarding-password.component.css",
})
export class OnboardingPasswordComponent implements OnInit {
  passwordForm: FormGroup;
  validations = { minLength: false, uppercase: false, number: false, specialChar: false };
  button_clicked = false;
  submitting = false;
  token: string | null = null;
  tokenValid: boolean | null = null;
  email: string | null = null;
  apiError: PasswordApiError | null = null;
  success = false;
  resending = false;
  resendSuccess = false;
  resendApiError: ActivationEmailApiError | null = null;
  resendAvailable = !!environment.resendActivationEmailSubscriptionKey;

  constructor(
    private fb: FormBuilder,
    private gigyaService: GigyaService,
    private route: ActivatedRoute,
    private router: Router,
    private passwordService: PasswordService,
    private credentialHandoff: CredentialHandoffService,
    private activationEmailService: ActivationEmailService,
  ) {
    this.passwordForm = this.fb.group({
      newPassword: ["", [Validators.required, this.passwordService.validator()]],
      confirmPassword: ["", [Validators.required]],
    });
    this.passwordForm.get("newPassword")?.valueChanges.subscribe(() => {
      if (this.apiError) {
        this.button_clicked = false;
        this.apiError = null;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const searchParams = new URLSearchParams(window.location.search);
    this.token = searchParams.get("token");
    this.email = searchParams.get("email");
    if (!this.token) {
      this.tokenValid = false;
      return;
    }
    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.password.validateResetToken, {
      passwordResetToken: this.token,
    });
    this.tokenValid = res['isValidToken'] === true;
  }

  get passwordMismatch(): boolean {
    const newPassword = this.passwordForm.get("newPassword")?.value;
    const confirmPassword = this.passwordForm.get("confirmPassword")?.value;
    return newPassword && confirmPassword && newPassword !== confirmPassword;
  }

  checkPassword() {
    const control = this.passwordForm.get("newPassword");
    this.validations = this.passwordService.checkStrength(control?.value || "");
    control?.updateValueAndValidity({ emitEvent: false });
  }

  async resendActivationEmail() {
    if (!this.email || this.resending) return;

    this.resendApiError = null;
    this.resendSuccess = false;
    this.resending = true;

    const error = await this.activationEmailService.resendActivationEmail(this.email);

    this.resending = false;
    if (error) {
      this.resendApiError = error;
      return;
    }
    this.resendSuccess = true;
  }

  async onSubmit() {
    if (!this.passwordForm.valid || this.passwordMismatch) return;

    const newPassword = this.passwordForm.get("newPassword")?.value;

    this.apiError = null;
    this.success = false;
    this.button_clicked = true;
    this.submitting = true;

    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.resetPassword, {
      loginID: this.email,
      newPassword,
      passwordResetToken: this.token,
    });

    if (res.errorCode !== 0) {
      this.submitting = false;
      this.button_clicked = false;
      this.apiError = this.passwordService.parseError(res);
      return;
    }

    this.credentialHandoff.stage(this.email!, newPassword);
    this.router.navigate(["/"], { queryParamsHandling: "preserve" });
  }
}
